import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart, Bar, Cell,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api from "../services/api";
import "./LaborHoursList.css";

function laborHoursGroupLabel(r) {
  const bu = (r.departmentBuName || r.departmentName || "").trim();
  const ka = (r.kaDisplay || r.kaName || "").trim();
  if (bu && ka) return `${bu} / ${ka}`;
  if (bu) return bu;
  if (ka) return ka;
  return "（未設定）";
}

function build14Months(endDateStr) {
  const endDateObj = new Date(`${endDateStr.slice(0, 10)}T12:00:00`);
  let y = endDateObj.getFullYear();
  let m = endDateObj.getMonth() + 1;
  const arr = [];
  for (let i = 0; i < 14; i++) {
    arr.unshift({ year: y, month: m, key: `${y}-${m}`, label: `${y}年${m}月` });
    m--;
    if (m <= 0) { m = 12; y--; }
  }
  return arr;
}

function calcTop5Bottom5FromRows(rows, monthKey) {
  const withHours = rows
    .map(r => ({
      name: r.name || r.memberId,
      departmentName: laborHoursGroupLabel(r),
      total_hours: Number(r.monthHours?.[monthKey]) || 0,
    }))
    .filter(e => e.total_hours > 0);
  const sorted = [...withHours].sort((a, b) => b.total_hours - a.total_hours);
  return {
    top5: sorted.slice(0, 5),
    bottom5: sorted.slice(-5).reverse(),
    employee_count: withHours.length,
  };
}

/** 部署・課ごとに固定色を割り当てるカラーパレット */
const PALETTE = [
  "#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#14b8a6",
  "#a855f7", "#0ea5e9", "#d97706", "#10b981", "#f43f5e",
  "#64748b", "#7c3aed", "#b45309", "#15803d", "#1d4ed8",
];

/** 名前→固定色マップを生成 */
function buildColorMap(names) {
  const map = {};
  names.forEach((n, i) => { map[n] = PALETTE[i % PALETTE.length]; });
  return map;
}

/** 各バーに Cell で色をつける水平棒グラフ */
const ColoredHBarChart = ({ data, colorMap, height = 40 }) => {
  const chartH = Math.max(200, data.length * height + 60);
  const maxLabelLen = Math.max(...data.map(d => (d.name || "").length), 4);
  const yWidth = Math.min(160, maxLabelLen * 13);

  return (
    <div style={{ width: "100%", height: chartH }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" unit="h" tick={{ fontSize: 11 }} />
          <YAxis
            type="category"
            dataKey="name"
            width={yWidth}
            tick={{ fontSize: 12, fill: "#1e293b" }}
          />
          <Tooltip
            formatter={(v, _name, props) => [
              `${Number(v).toFixed(2)}h`,
              props.payload?.name || "平均時間",
            ]}
          />
          <Bar dataKey="平均時間" barSize={height - 10} isAnimationActive={false}>
            {data.map((entry, i) => (
              <Cell key={i} fill={colorMap[entry.name] || PALETTE[i % PALETTE.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** カラー凡例 */
const ColorLegend = ({ data, colorMap }) => (
  <div style={{
    display: "flex", flexWrap: "wrap", gap: "6px 16px",
    marginTop: 12, fontSize: "0.82rem",
  }}>
    {data.map((d, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{
          display: "inline-block", width: 12, height: 12, borderRadius: 2,
          background: colorMap[d.name] || PALETTE[i % PALETTE.length], flexShrink: 0,
        }} />
        <span style={{ color: "#374151" }}>{d.name}</span>
        <span style={{ color: "#64748b", fontWeight: 600 }}>{d.平均時間}h</span>
      </div>
    ))}
  </div>
);

const LaborHoursAnalytics = () => {
  const location = useLocation();
  const stateDept = location.state?.departmentName ?? "";
  const stateEndDate = location.state?.printEndDate ?? "";
  const [departmentName, setDepartmentName] = useState(stateDept);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const endDate = stateEndDate || new Date().toISOString().slice(0, 10);

  const months14 = useMemo(() => build14Months(endDate), [endDate]);

  const [avgTrend, setAvgTrend] = useState([]);
  const [avgTrendLoading, setAvgTrendLoading] = useState(true);

  const prevMonthInfo = months14.length >= 2 ? months14[months14.length - 2] : null;
  const prevMonthLabel = prevMonthInfo
    ? `${prevMonthInfo.year}年${prevMonthInfo.month}月`
    : "前月";

  const curMonthInfo = months14.length >= 1 ? months14[months14.length - 1] : null;
  const curMonthLabel = curMonthInfo
    ? `${curMonthInfo.year}年${curMonthInfo.month}月`
    : "当月";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/api/worktime/admin/labor-hours-list/", {
        params: { ...(departmentName ? { departmentName } : {}), endDate },
      })
      .then((res) => { if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [departmentName, endDate]);

  useEffect(() => {
    let cancelled = false;
    setAvgTrendLoading(true);
    api.get("/api/admin/monthly-avg-hours/")
      .then((res) => { if (!cancelled) setAvgTrend(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setAvgTrend([]); })
      .finally(() => { if (!cancelled) setAvgTrendLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // 月次合計（全員）14か月
  const byMonthSum = useMemo(() => {
    const acc = {};
    for (const { key } of months14) acc[key] = 0;
    for (const r of rows) {
      for (const { key } of months14) {
        const v = r.monthHours?.[key];
        if (v != null) acc[key] += Number(v);
      }
    }
    return months14.map(({ key, label }) => ({
      name: label,
      合計時間: Math.round(acc[key] * 100) / 100,
    }));
  }, [rows, months14]);

  // 月次平均（1人あたり）14か月
  const byMonthAvg = useMemo(() => {
    const n = rows.length;
    const acc = {};
    for (const { key } of months14) acc[key] = 0;
    for (const r of rows) {
      for (const { key } of months14) {
        const v = r.monthHours?.[key];
        if (v != null) acc[key] += Number(v);
      }
    }
    return months14.map(({ key, label }) => ({
      name: label,
      平均時間: n > 0 ? Math.round((acc[key] / n) * 100) / 100 : 0,
    }));
  }, [rows, months14]);

  // Top5/Bottom5
  const curTopBottom = useMemo(() => {
    if (!curMonthInfo) return null;
    return calcTop5Bottom5FromRows(rows, curMonthInfo.key);
  }, [rows, curMonthInfo]);

  const prevTopBottom = useMemo(() => {
    if (!prevMonthInfo) return null;
    return calcTop5Bottom5FromRows(rows, prevMonthInfo.key);
  }, [rows, prevMonthInfo]);

  // ── 部署別（bu）平均 ──────────────────────────────
  const byBuAvg = useMemo(() => {
    if (!prevMonthInfo) return [];
    const prevKey = prevMonthInfo.key;
    const sumMap = new Map();
    const cntMap = new Map();
    for (const r of rows) {
      const bu = (r.departmentBuName || r.departmentName || "（未設定）").trim();
      const h = Number(r.monthHours?.[prevKey]) || 0;
      sumMap.set(bu, (sumMap.get(bu) || 0) + h);
      cntMap.set(bu, (cntMap.get(bu) || 0) + 1);
    }
    return Array.from(sumMap.entries())
      .map(([name, sum]) => ({ name, 平均時間: Math.round((sum / (cntMap.get(name) || 1)) * 100) / 100 }))
      .sort((a, b) => b.平均時間 - a.平均時間);
  }, [rows, prevMonthInfo]);

  // ── 課別（ka）平均 ───────────────────────────────
  const byKaAvg = useMemo(() => {
    if (!prevMonthInfo) return [];
    const prevKey = prevMonthInfo.key;
    const sumMap = new Map();
    const cntMap = new Map();
    for (const r of rows) {
      const ka = (r.kaDisplay || r.kaName || "").trim();
      if (!ka) continue;
      const h = Number(r.monthHours?.[prevKey]) || 0;
      sumMap.set(ka, (sumMap.get(ka) || 0) + h);
      cntMap.set(ka, (cntMap.get(ka) || 0) + 1);
    }
    return Array.from(sumMap.entries())
      .map(([name, sum]) => ({ name, 平均時間: Math.round((sum / (cntMap.get(name) || 1)) * 100) / 100 }))
      .sort((a, b) => b.平均時間 - a.平均時間);
  }, [rows, prevMonthInfo]);

  const buColorMap = useMemo(() => buildColorMap(byBuAvg.map(d => d.name)), [byBuAvg]);
  const kaColorMap = useMemo(() => buildColorMap(byKaAvg.map(d => d.name)), [byKaAvg]);

  /** Top5/Bottom5 描画 */
  const renderTopBottom = (tb, monthLabel, colorTop, colorBottom) => {
    if (!tb) return <p style={{ color: "#64748b" }}>データがありません。</p>;
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: "#ecfdf5", border: `1px solid ${colorTop}`, borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ fontWeight: 700, color: "#065f46", marginBottom: 8, fontSize: "0.95rem" }}>
            ⬆ 勤務時間多い上位5名（{monthLabel}）
          </div>
          <div style={{ fontSize: "0.82rem", color: "#047857", marginBottom: 10 }}>対象社員: {tb.employee_count}名</div>
          {tb.top5.length === 0 ? <p style={{ color: "#64748b", fontSize: "0.85rem" }}>データなし</p> : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
              <thead>
                <tr style={{ background: "#d1fae5" }}>
                  <th style={{ padding: "4px 6px", textAlign: "left", borderBottom: `1px solid ${colorTop}` }}>順位</th>
                  <th style={{ padding: "4px 6px", textAlign: "left", borderBottom: `1px solid ${colorTop}` }}>氏名</th>
                  <th style={{ padding: "4px 6px", textAlign: "left", borderBottom: `1px solid ${colorTop}` }}>部署</th>
                  <th style={{ padding: "4px 6px", textAlign: "right", borderBottom: `1px solid ${colorTop}` }}>勤務時間</th>
                </tr>
              </thead>
              <tbody>
                {tb.top5.map((e, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #a7f3d0" }}>
                    <td style={{ padding: "4px 6px", fontWeight: 700, color: "#059669" }}>#{i + 1}</td>
                    <td style={{ padding: "4px 6px" }}>{e.name}</td>
                    <td style={{ padding: "4px 6px", color: "#64748b", fontSize: "0.78rem" }}>{e.departmentName}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600 }}>{e.total_hours.toFixed(2)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ background: "#fff7ed", border: `1px solid ${colorBottom}`, borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ fontWeight: 700, color: "#7c2d12", marginBottom: 8, fontSize: "0.95rem" }}>
            ⬇ 勤務時間少ない下位5名（{monthLabel}）
          </div>
          <div style={{ fontSize: "0.82rem", color: "#c2410c", marginBottom: 10 }}>対象社員: {tb.employee_count}名</div>
          {tb.bottom5.length === 0 ? <p style={{ color: "#64748b", fontSize: "0.85rem" }}>データなし</p> : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
              <thead>
                <tr style={{ background: "#ffedd5" }}>
                  <th style={{ padding: "4px 6px", textAlign: "left", borderBottom: `1px solid ${colorBottom}` }}>順位</th>
                  <th style={{ padding: "4px 6px", textAlign: "left", borderBottom: `1px solid ${colorBottom}` }}>氏名</th>
                  <th style={{ padding: "4px 6px", textAlign: "left", borderBottom: `1px solid ${colorBottom}` }}>部署</th>
                  <th style={{ padding: "4px 6px", textAlign: "right", borderBottom: `1px solid ${colorBottom}` }}>勤務時間</th>
                </tr>
              </thead>
              <tbody>
                {tb.bottom5.map((e, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #fed7aa" }}>
                    <td style={{ padding: "4px 6px", fontWeight: 700, color: "#ea580c" }}>#{i + 1}</td>
                    <td style={{ padding: "4px 6px" }}>{e.name}</td>
                    <td style={{ padding: "4px 6px", color: "#64748b", fontSize: "0.78rem" }}>{e.departmentName}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600 }}>{e.total_hours.toFixed(2)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="worktimes-container labor-hours-list" style={{ paddingBottom: 40 }}>
      <div className="labor-hours-list-header">
        <Link to="/admin/labor-hours-list" className="labor-hours-list-back">← 労働時間一覧に戻る</Link>
        <h2 style={{ textAlign: "center", color: "#555" }}>労働時間分析</h2>
        <p style={{ textAlign: "center", color: "#64748b" }}>
          集計は {endDate} まで / {departmentName ? `部署: ${departmentName}` : "全社"}
        </p>
        <label style={{ display: "block", textAlign: "center", marginTop: 8 }}>
          部署で絞る{" "}
          <input
            type="text"
            value={departmentName}
            onChange={(e) => setDepartmentName(e.target.value)}
            placeholder="空欄で全社"
            style={{ padding: 6, minWidth: 200 }}
          />
        </label>
      </div>

      {/* 月次平均トレンド */}
      <section style={{ marginBottom: 40 }}>
        <h3 style={{ fontSize: "1.1rem", margin: "24px 0 8px" }}>月次平均労働時間トレンド</h3>
        <button
          style={{ marginBottom: 12, padding: "5px 14px", background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: "0.85rem" }}
          onClick={() => {
            api.post("/api/admin/monthly-avg-hours/compute/").then(() => {
              api.get("/api/admin/monthly-avg-hours/").then(r => setAvgTrend(Array.isArray(r.data) ? r.data : []));
            });
          }}
        >再集計</button>
        {avgTrendLoading ? <p style={{ color: "#64748b" }}>読み込み中...</p> :
          avgTrend.length === 0 ? <p style={{ color: "#64748b" }}>データがありません。</p> : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={avgTrend} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis unit="h" />
                  <Tooltip formatter={(v) => [`${v}h`, "平均労働時間"]} />
                  <Legend />
                  <Line type="monotone" dataKey="avg_hours" name="月平均(h)" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
      </section>

      {loading ? <p className="labor-hours-list-loading">読み込み中...</p> : (
        <>
          {/* Top5/Bottom5 前月 */}
          {prevMonthInfo && (
            <section style={{ marginBottom: 40 }}>
              <h3 style={{ fontSize: "1.1rem", margin: "24px 0 8px" }}>
                {prevMonthLabel} — 勤務時間多い上位5名 / 少ない下位5名
              </h3>
              {renderTopBottom(prevTopBottom, prevMonthLabel, "#6ee7b7", "#fed7aa")}
            </section>
          )}

          {/* Top5/Bottom5 当月 */}
          {curMonthInfo && (
            <section style={{ marginBottom: 40 }}>
              <h3 style={{ fontSize: "1.1rem", margin: "24px 0 8px" }}>
                {curMonthLabel} — 勤務時間多い上位5名 / 少ない下位5名
              </h3>
              {renderTopBottom(curTopBottom, curMonthLabel, "#6ee7b7", "#fed7aa")}
            </section>
          )}

          {/* 月次合計 14か月 */}
          <section style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.1rem", margin: "24px 0 12px" }}>
              月次合計（全員の合算）
              {months14.length > 0 && (
                <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 400, marginLeft: 8 }}>
                  {months14[0].year}年{months14[0].month}月〜{months14[months14.length - 1].year}年{months14[months14.length - 1].month}月
                </span>
              )}
            </h3>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={byMonthSum} margin={{ top: 8, right: 16, left: 8, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis unit="h" />
                  <Tooltip />
                  <Bar dataKey="合計時間" fill="#0ea5e9" name="合計時間(h)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* 月次平均 14か月 */}
          <section style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: "1.1rem", margin: "24px 0 12px" }}>月次平均（1人あたり）</h3>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={byMonthAvg} margin={{ top: 8, right: 16, left: 8, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis unit="h" />
                  <Tooltip />
                  <Bar dataKey="平均時間" fill="#14b8a6" name="平均(h)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* ── 部署別 平均労働時間 ── */}
          <section style={{ marginBottom: 48 }}>
            <h3 style={{ fontSize: "1.1rem", margin: "24px 0 4px" }}>
              部署別 平均労働時間（{prevMonthLabel}）
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.88rem", margin: "0 0 10px" }}>
              各部署に所属する社員の {prevMonthLabel} 勤務時間の平均です。部署ごとに色を分けています。
            </p>
            {byBuAvg.length === 0 ? (
              <p style={{ color: "#64748b" }}>データがありません。</p>
            ) : (
              <>
                <ColoredHBarChart data={byBuAvg} colorMap={buColorMap} height={36} />
                <ColorLegend data={byBuAvg} colorMap={buColorMap} />
              </>
            )}
          </section>

          {/* ── 課別 平均労働時間 ── */}
          <section style={{ marginBottom: 48 }}>
            <h3 style={{ fontSize: "1.1rem", margin: "24px 0 4px" }}>
              課別 平均労働時間（{prevMonthLabel}）
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.88rem", margin: "0 0 10px" }}>
              各課に所属する社員の {prevMonthLabel} 勤務時間の平均です。課ごとに色を分けています。
            </p>
            {byKaAvg.length === 0 ? (
              <p style={{ color: "#64748b" }}>課データがありません。</p>
            ) : (
              <>
                <ColoredHBarChart data={byKaAvg} colorMap={kaColorMap} height={36} />
                <ColorLegend data={byKaAvg} colorMap={kaColorMap} />
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default LaborHoursAnalytics;
